import { ElLoading, ElNotification, ElMessageBox } from 'element-plus'
import { setI18nLanguage, getLocale, detectLanguage } from '@/locale/i18n'
import type { Ref } from 'vue'
import eventBus from 'vue3-eventbus'

export const loading = (options: any) => {
  return ElLoading.service(options)
}

/**
 * Notification-based message function
 * @param msg - Message content
 * @param type - Message type
 * @param title - Optional title
 */
const notify = (
  msg: string,
  type: 'success' | 'error' | 'warning' | 'info' = 'success',
  title?: string
) => {
  ElNotification({
    title: title || type.charAt(0).toUpperCase() + type.slice(1),
    message: msg,
    type: type,
    duration: 3000
  })
}

export const message = {
  notify,
  success: (msg: string, title?: string) => notify(msg, 'success', title),
  error: (msg: string, title?: string) => notify(msg, 'error', title),
  warning: (msg: string, title?: string) => notify(msg, 'warning', title),
  info: (msg: string, title?: string) => notify(msg, 'info', title)
}

let i18nInstance: any = null
let localeRef: Ref<any> | null = null

export const initLocale = async (userLocale?: string) => {
  const lang = userLocale || detectLanguage()
  await setLocale(lang)
  return lang
}

export const setLocale = async (lang: string) => {
  const localeConfig = await getLocale(lang)

  if (i18nInstance) {
    await setI18nLanguage(i18nInstance, localeConfig)
  }

  if (localeRef) {
    localeRef.value = localeConfig.elMessage
  }

  return localeConfig
}

export const setupLocale = (i18n: any, locale: Ref<any>) => {
  i18nInstance = i18n
  localeRef = locale
}

export { eventBus }

export const prompt = (message: string, defaultValue: string = ''): Promise<string> => {
  //use ElMessageBox.prompt to show a prompt dialog
  return new Promise((resolve, reject) => {
    ElMessageBox.prompt(message, 'Input Required', {
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      inputValue: defaultValue,
      inputPattern: /.+/,
      inputErrorMessage: 'Input cannot be empty'
    })
      .then(({ value }) => resolve(value))
      .catch(() => reject('Prompt cancelled'))
  })
}

/**
 * Safely clone an object, handling circular references
 * @param obj - The object to clone
 * @returns A deep clone of the object with circular references handled
 */
export const safeClone = <T>(obj: T): T => {
  const seen = new WeakSet()
  
  const clone = (value: any): any => {
    if (value === null || typeof value !== 'object') {
      return value
    }
    
    if (seen.has(value)) {
      return '[Circular]'
    }
    
    seen.add(value)
    
    if (Array.isArray(value)) {
      const cloned = value.map(clone)
      seen.delete(value)
      return cloned
    }
    
    const cloned = {} as any
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        cloned[key] = clone(value[key])
      }
    }
    
    seen.delete(value)
    return cloned
  }
  
  return clone(obj)
}